import type { FC } from "react";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { DefaultInputMax, DefaultInputMin, DefaultInputStep } from "@/components/inputs/shared.ts";

interface CounterProps {
  min?: number;
  max?: number;
  id?: string;
  step?: number;
  state: number;
  onChange: (newCount: number) => void;
}

const CounterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${t.brandBorderSegment};
  border-radius: 9999px;
`;

const SpanAdjustment = styled.span`
  padding-bottom: 0.1rem;
`;

const CounterInput = styled.input`
  width: 2rem;
  align-self: stretch;
  border: none;
  border-left: 1px solid ${t.brandBorderColor};
  border-right: 1px solid ${t.brandBorderColor};
  text-align: center;
  padding: 0;

  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
  }
`;

const ButtonEmptyWrapper = styled.button`
  display: flex;
  text-align: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0.1rem 0.5rem;

  font-size: var(--16px);
`;

enum ActionTypes {
  Min,
  Add,
  Set,
}

export const Counter: FC<CounterProps> = ({ min, max, step, state, onChange, id }) => {
  const onUpdate = (action: ActionTypes, newValue?: number) => {
    switch (action) {
      case ActionTypes.Min:
        onChange(Math.max(min ?? DefaultInputMin, state - 1));
        break;

      case ActionTypes.Add:
        onChange(Math.min(max ?? DefaultInputMax, state + 1));
        break;

      case ActionTypes.Set:
        onChange(
          Math.max(min ?? DefaultInputMin, Math.min(max ?? DefaultInputMax, newValue ?? state)),
        );
        break;
    }
  };

  return (
    <CounterWrapper>
      <ButtonEmptyWrapper
        onClick={() => {
          onUpdate(ActionTypes.Min);
        }}
      >
        <SpanAdjustment>-</SpanAdjustment>
      </ButtonEmptyWrapper>
      <CounterInput
        id={id}
        value={state}
        placeholder={state.toString()}
        onChange={(e) => {
          onUpdate(ActionTypes.Set, Number(e.target.value));
        }}
        step={step ?? DefaultInputStep}
        // Note: Use 'Number' instead of 'number'
        // React (?) will remove leading 0 in that case
        // Without it, users may end up with 01, or 02, instead of 1, 2
        type={"Number"}
        max={max ?? DefaultInputMax}
        min={min ?? DefaultInputMin}
      />
      <ButtonEmptyWrapper
        onClick={() => {
          onUpdate(ActionTypes.Add);
        }}
      >
        <SpanAdjustment>+</SpanAdjustment>
      </ButtonEmptyWrapper>
    </CounterWrapper>
  );
};
